import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EntityService } from '@core/services/entity/entity.service';
import { HeaderService } from '@shared/services';
import { cloneDeep } from 'lodash-es';
import { delay, tap } from 'rxjs';
import { Entity } from 'src/app/modules/portfolio/models/entity';

@Component({
  standalone: false,
  selector: 'gstar-entities-combobox',
  templateUrl: './entities-combobox.component.html',
  styleUrls: ['./entities-combobox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntitiesComboboxComponent implements OnInit {
  private readonly entityService = inject(EntityService);
  private readonly headerService = inject(HeaderService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() value!: Entity | null | undefined;
  @Input() required?: boolean;
  @Input() disabled?: boolean;
  @Input() preserveSelectionOnClientChange: boolean = false;

  @Output() selectEntity: EventEmitter<Entity | null | undefined> =
    new EventEmitter();
  @Output() clearEntity: EventEmitter<void> = new EventEmitter();
  public entities: WritableSignal<Entity[]> = signal<Entity[]>([]);
  isEntityListLoading = signal<boolean>(true);

  selectedClient$ = toObservable(this.headerService.selectedClient);

  ngOnInit(): void {
    this.selectedClient$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.preserveSelectionOnClientChange) {
          return;
        }

        this.clearEntityHandler();
      });

    this.entityService.entitiesByClientId
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.isEntityListLoading.set(true)),
        tap(entitiesResponse => this.entities.set(cloneDeep(entitiesResponse))),
        delay(0), // Due to Appkit bug, we need to add a delay to force the re-rendering of combobox
        tap(() => this.isEntityListLoading.set(false))
      )
      .subscribe();
  }

  selectEntityHandler(entity: Entity | null | undefined): void {
    this.value = entity;
    this.selectEntity.emit(entity);
  }

  clearEntityHandler(): void {
    this.value = null;
    this.clearEntity.emit();
  }
}
